import { useState } from "react";
import { toast } from "sonner";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import Reveal from "@/components/Reveal";
import { submitLead } from "@/lib/api";

const initial = {
  name: "",
  email: "",
  phone: "",
  event_type: "",
  guest_count: "",
  message: "",
};

const EVENT_TYPES = [
  "Corporate Event",
  "Festival",
  "Neighborhood Event",
  "Graduation Party",
  "Birthday Party",
  "Wedding",
  "Other",
];

export default function Book() {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState();
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({
        type: "booking",
        ...form,
        event_date: date ? format(date, "yyyy-MM-dd") : "",
      });
      toast.success("Booking request received. We'll confirm availability shortly.");
      setForm(initial);
      setDate(undefined);
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="book-page" className="bg-[#121212]">
      <section className="pt-20 md:pt-28 pb-12">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A] mb-5">Book the Truck</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.98] text-[#F5ECD7]">
            Pick a date.
            <br />
            <span className="font-serif-italic font-normal text-[#C99C3A]">We'll bring the flavor.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[#F5ECD7]/65 text-lg">
            Perfect for corporate events, festivals, neighborhood gatherings,
            graduations, birthdays, and weddings. Charlotte & surrounding NC/SC area.
          </p>
        </div>
      </section>

      <section className="section-pad pt-0">
        <div className="max-w-5xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="bg-[#1A1A1A] border border-[#F5ECD7]/10 rounded-2xl p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#C99C3A] mb-3">Choose Date</p>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="book-date-trigger"
                    className="w-full flex items-center justify-between border border-[#F5ECD7]/15 px-4 py-3 text-left text-[#F5ECD7] hover:border-[#C99C3A] transition-colors"
                  >
                    <span>{date ? format(date, "PPP") : "Select event date"}</span>
                    <CalendarIcon size={16} className="text-[#C99C3A]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-[#F5ECD7]/15" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="text-[#F5ECD7]" />
                </PopoverContent>
              </Popover>
              <p className="mt-6 text-xs text-[#F5ECD7]/45 leading-relaxed">
                Most bookings are confirmed within one business day. For events under 14 days out, please call.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120} className="md:col-span-7">
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              <input data-testid="book-name" required placeholder="Full Name *" value={form.name} onChange={set("name")} className="input-line md:col-span-2" />
              <input data-testid="book-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className="input-line md:col-span-1" />
              <input data-testid="book-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className="input-line md:col-span-1" />
              <select data-testid="book-event-type" value={form.event_type} onChange={set("event_type")} className="input-line md:col-span-1 bg-[#121212]">
                <option value="">Event Type</option>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input data-testid="book-count" placeholder="Estimated Guests" value={form.guest_count} onChange={set("guest_count")} className="input-line md:col-span-1" />
              <textarea data-testid="book-message" rows={4} placeholder="Anything we should know? Venue, hours, vibe..." value={form.message} onChange={set("message")} className="input-line md:col-span-2 resize-none" />
              <div className="md:col-span-2 mt-6">
                <button data-testid="book-submit" type="submit" disabled={loading} className="btn-gold">
                  {loading ? "Sending..." : "Request Booking"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
